/* eslint-disable no-empty */
import { getEmptyParagraphNode } from '@doltech/utils/lib/editor';
import { Editor, Element, Path, Transforms } from 'slate';
import { ReactEditor } from 'slate-react';

import {
  ELEMENT_H1,
  ELEMENT_H2,
  ELEMENT_H3,
  ELEMENT_H4,
  ELEMENT_H5,
  ELEMENT_H6,
  ELEMENT_PARAGRAPH,
  ELEMENT_TABLE,
} from '../../../slatePlugins';
import { ELEMENT_BORDER_SHADING } from '../../DolEditor/elements/border-shading/defaults';
import { ELEMENT_DIVIDER } from '../../DolEditor/elements/divider/defaults';
import { MediaType } from '../../DolEditor/elements/media-constant';
import {
  ELEMENT_BULLET_POINT,
  ELEMENT_BULLET_POINT_CHECK,
  ELEMENT_BULLET_POINT_LIST_ITEM,
  ELEMENT_BULLET_POINT_NUMBER,
  ELEMENT_BULLET_POINT_QUESTION,
  ELEMENT_BULLET_POINT_X,
} from './BulletPoint/BulletPointPlugin.types';
import { CARD_LOGIC_ELEMENT_TYPE } from './CardLogic/model/constants';
import { EXERCISE_GAP_FILL_WITH_HINT } from './ExerciseGapFillWithHint/types';
import { K12_ELEMENT_QUOTE } from './K12Quote/K12QuotePlugin.types';
import { ELEMENT_QUOTE } from './Quote/QuotePlugin.types';
import { ELEMENT_IELTS_CALENDAR_COMPUTER_BASED } from './SpecialObject/IeltsCalendar/ComputerBased/IeltsCalendarComputerBasedPlugin.types';
import { ELEMENT_IELTS_PURPOSE_CONVERTER } from './SpecialObject/IeltsConverter/Purpose/IeltsPurposeConverterPlugin.types';
import { ELEMENT_IMAGE_SLIDE_SHOW } from './SpecialObject/ImageSlide/SlideShow/ImageSlideShowPlugin.types';
import { ELEMENT_IMAGE_SLIDE_TEXT } from './SpecialObject/ImageSlide/SlideText/ImageSlideTextPlugin.types';

const INSERT_PARAGRAPH_AT_END_EDITOR_NODES = [
  ELEMENT_PARAGRAPH,
  ELEMENT_H1,
  ELEMENT_H2,
  ELEMENT_H3,
  ELEMENT_H4,
  ELEMENT_H5,
  ELEMENT_H6,
  K12_ELEMENT_QUOTE,
  ELEMENT_BULLET_POINT,
  ELEMENT_BULLET_POINT_CHECK,
  ELEMENT_BULLET_POINT_X,
  ELEMENT_BULLET_POINT_QUESTION,
  ELEMENT_BULLET_POINT_NUMBER,
  ELEMENT_BULLET_POINT_LIST_ITEM,
];

const INSERT_PARAGRAPH_BETWEEN_THOSE_NODES = [
  ELEMENT_TABLE,
  ELEMENT_DIVIDER,
  CARD_LOGIC_ELEMENT_TYPE,
  ELEMENT_QUOTE,
  MediaType.FILE,
  MediaType.VIDEO,
  MediaType.AUDIO,
  MediaType.IMAGE,
  MediaType.CONVERTER,
  MediaType.CALCULATOR,
  ELEMENT_BORDER_SHADING,
  MediaType.PAPER_EXAM,
  ELEMENT_IELTS_CALENDAR_COMPUTER_BASED,
  ELEMENT_IELTS_PURPOSE_CONVERTER,
  MediaType.VOCAB_CARD,
  MediaType.VOCAB_LIST,
  MediaType.VOCAB_LIST_PRE,
  ELEMENT_IMAGE_SLIDE_SHOW,
  ELEMENT_IMAGE_SLIDE_TEXT,
  MediaType.VOCAB_LIST_NEWS,
  EXERCISE_GAP_FILL_WITH_HINT,
  K12_ELEMENT_QUOTE,
];

const isWhiteListNodeType = (nodeList, node) => nodeList.some((t) => t === (node as any)?.type);

export const withParagraphAtEditorEnd =
  () =>
  <T extends ReactEditor>(editor: T) => {
    const { normalizeNode } = editor;

    editor.normalizeNode = (entry) => {
      const [node, path] = entry as [any, any[]];

      // recover empty state
      if ((node?.children as any)?.length === 0 && path.length === 0) {
        Transforms.insertNodes(editor, getEmptyParagraphNode());
      }

      if (
        Element.isElement(node) &&
        path.length === 1 &&
        isWhiteListNodeType(INSERT_PARAGRAPH_BETWEEN_THOSE_NODES, node)
      ) {
        let findNextNode = null;
        try {
          findNextNode = Editor.next(editor, { at: path });
          // eslint-disable-next-line no-empty
        } catch (ex) {}
        if (findNextNode) {
          const [nextNode, nextNodePath] = findNextNode;
          if (
            Element.isElement(nextNode) &&
            nextNodePath.length === 1 &&
            nextNode?.type &&
            isWhiteListNodeType(INSERT_PARAGRAPH_BETWEEN_THOSE_NODES, nextNode)
          ) {
            const paragraphPath = Path.next(nextNodePath);
            Transforms.insertNodes(editor, getEmptyParagraphNode(), {
              at: paragraphPath,
            });
            Transforms.moveNodes(editor, { at: paragraphPath, to: nextNodePath });
            return;
          }
        }
      }
      if (path.length === 0 || path.length === 1) {
        try {
          const endOfDoc = Editor.end(editor, []);
          const [endNode, endNodePath] = Editor.node(editor, endOfDoc, { depth: 1 }) as any;
          if (!isWhiteListNodeType(INSERT_PARAGRAPH_AT_END_EDITOR_NODES, endNode)) {
            Transforms.insertNodes(editor, getEmptyParagraphNode(), { at: Path.next(endNodePath) });
            return;
          }
        } catch (ex) {}

        try {
          const startOfDoc = Editor.start(editor, []);
          const [startNode, startNodePath] = Editor.node(editor, startOfDoc, { depth: 1 }) as any;
          if (!isWhiteListNodeType(INSERT_PARAGRAPH_AT_END_EDITOR_NODES, startNode)) {
            Transforms.insertNodes(editor, getEmptyParagraphNode(), { at: startNodePath });
            return;
          }
        } catch (ex) {}
      }

      normalizeNode(entry);
    };

    return editor;
  };
